import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';

type BookingRejectionDialogProps = {
  open: boolean;
  title: string;
  description: string;
  reason: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  reasonHelperText: string;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  loading: boolean;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  notice?: React.ReactNode;
  confirmColor?: 'error' | 'warning';
  fullScreen?: boolean;
};

const REASON_MAX_LENGTH = 1000;

const BookingRejectionDialog: React.FC<BookingRejectionDialogProps> = ({
  open,
  title,
  description,
  reason,
  reasonLabel,
  reasonPlaceholder,
  reasonHelperText,
  confirmLabel,
  cancelLabel,
  closeLabel,
  loading,
  onReasonChange,
  onClose,
  onConfirm,
  notice,
  confirmColor = 'error',
  fullScreen = false,
}) => {
  const trimmedReason = reason.trim();
  const reasonMissing = !trimmedReason;
  const alertSeverity = confirmColor === 'warning' ? 'warning' : 'error';

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!loading) onClose();
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="booking-rejection-dialog-title"
      PaperProps={{
        sx: (theme) => ({
          borderRadius: { xs: 0, sm: 2.5 },
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
          backgroundImage: 'none',
          bgcolor: theme.palette.background.paper,
        }),
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          px: { xs: 2.25, sm: 3 },
          py: 2.25,
          background: `linear-gradient(180deg, ${alpha(theme.palette[confirmColor].main, 0.14)} 0%, transparent 100%)`,
        })}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={(theme) => ({
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette[confirmColor].main, 0.18),
              color: theme.palette[confirmColor].main,
              border: `1px solid ${alpha(theme.palette[confirmColor].main, 0.35)}`,
              flexShrink: 0,
              mt: 0.15,
            })}
          >
            <EventBusyRoundedIcon fontSize="small" />
          </Box>
          <Typography
            id="booking-rejection-dialog-title"
            variant="h6"
            component="h2"
            sx={{
              flex: 1,
              minWidth: 0,
              pt: 0.6,
              fontWeight: 700,
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label={closeLabel}
            onClick={onClose}
            disabled={loading}
            edge="end"
            size="small"
            sx={{ mt: 0.25 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.25, sm: 3 }, pt: 0.5, pb: 2.5 }}>
        <Stack spacing={2}>
          <Alert
            severity={alertSeverity}
            variant="outlined"
            sx={(theme) => ({
              alignItems: 'flex-start',
              borderRadius: 2,
              bgcolor: alpha(theme.palette[confirmColor].main, 0.08),
              borderColor: alpha(theme.palette[confirmColor].main, 0.28),
              color: 'text.primary',
              '& .MuiAlert-icon': {
                color: `${confirmColor}.main`,
                pt: 0.35,
              },
              '& .MuiAlert-message': {
                lineHeight: 1.55,
                fontSize: '0.875rem',
              },
            })}
          >
            {description}
          </Alert>
          {notice}
          <TextField
            autoFocus
            label={reasonLabel}
            placeholder={reasonPlaceholder}
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            required
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            helperText={
              <Stack direction="row" justifyContent="space-between" spacing={1} component="span">
                <Box component="span">{reasonHelperText}</Box>
                <Box component="span" sx={{ opacity: 0.7, flexShrink: 0 }}>
                  {reason.length}/{REASON_MAX_LENGTH}
                </Box>
              </Stack>
            }
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            FormHelperTextProps={{
              sx: { mx: 0, mt: 0.75 },
            }}
            sx={(theme) => ({
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.common.white, 0.03),
              },
            })}
            inputProps={{ maxLength: REASON_MAX_LENGTH }}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={(theme) => ({
          px: { xs: 2.25, sm: 3 },
          py: 2,
          gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.common.white, 0.02),
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'stretch',
          '& > :not(style) ~ :not(style)': { ml: 0 },
        })}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          variant="text"
          color="inherit"
          sx={{ color: 'text.secondary', alignSelf: { sm: 'center' } }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={reasonMissing || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={(theme) => ({
            minWidth: { sm: 168 },
            fontWeight: 700,
            boxShadow: 'none',
            ...(confirmColor === 'warning' && {
              color: theme.palette.getContrastText(theme.palette.warning.main),
              '&.Mui-disabled': {
                bgcolor: alpha(theme.palette.warning.main, 0.28),
                color: alpha(theme.palette.common.white, 0.55),
              },
            }),
            ...(confirmColor === 'error' && {
              '&.Mui-disabled': {
                bgcolor: alpha(theme.palette.error.main, 0.28),
                color: alpha(theme.palette.common.white, 0.55),
              },
            }),
          })}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingRejectionDialog;
